import { ReactNode } from 'react'
import { TipoLocale, FasciaPrezzo, TIPI_LOCALE_LABELS } from '../../types'

type BadgeVariant = 'default' | 'gold' | 'success' | 'warning' | 'danger' | 'info'
type BadgeSize = 'sm' | 'md'

interface BadgeProps {
  children: ReactNode
  variant?: BadgeVariant
  size?: BadgeSize
  className?: string
}

/**
 * Badge generico per etichette e tag
 */
export default function Badge({
  children,
  variant = 'default',
  size = 'sm',
  className = '',
}: BadgeProps) {
  // Stili per variante
  const variantStyles: Record<BadgeVariant, string> = {
    default: 'bg-gray-700 text-gray-200',
    gold: 'bg-gold/20 text-gold border border-gold/30',
    success: 'bg-green-900/30 text-green-400 border border-green-700/30',
    warning: 'bg-yellow-900/30 text-yellow-400 border border-yellow-700/30',
    danger: 'bg-red-900/30 text-red-400 border border-red-700/30',
    info: 'bg-blue-900/30 text-blue-400 border border-blue-700/30',
  }

  // Stili per dimensione
  const sizeStyles: Record<BadgeSize, string> = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
  }

  return (
    <span
      className={`inline-flex items-center font-medium rounded-full ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
    >
      {children}
    </span>
  )
}

/**
 * Badge specifico per tipo locale
 */
export function TipoLocaleBadge({
  tipo,
  size = 'sm',
}: {
  tipo: TipoLocale
  size?: BadgeSize
}) {
  // Colori per tipo locale
  const tipoColors: Record<TipoLocale, BadgeVariant> = {
    ristorante: 'info',
    trattoria: 'gold',
    agriturismo: 'success',
    osteria: 'warning',
    pizzeria: 'danger',
    pub: 'default',
  }

  return (
    <Badge variant={tipoColors[tipo]} size={size}>
      {TIPI_LOCALE_LABELS[tipo]}
    </Badge>
  )
}

/**
 * Badge per fascia di prezzo
 */
export function FasciaPrezzoBadge({
  fascia,
  size = 'sm',
}: {
  fascia: FasciaPrezzo
  size?: BadgeSize
}) {
  return (
    <Badge variant="gold" size={size}>
      {fascia}
    </Badge>
  )
}

/**
 * Badge "In Evidenza"
 */
export function InEvidenzaBadge({ size = 'sm' }: { size?: BadgeSize }) {
  return (
    <Badge variant="gold" size={size} className="gap-1">
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
      In evidenza
    </Badge>
  )
}

/**
 * Badge per parcheggio grande
 */
export function ParcheggioGrandeBadge({ size = 'sm' }: { size?: BadgeSize }) {
  return (
    <Badge variant="success" size={size} className="gap-1">
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
      Parcheggio 4x4
    </Badge>
  )
}
